import React, { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, Globe, ChevronUp, ChevronDown, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const EMPTY = {
  title: "", slug: "", location: "", country: "", destination_name: "", coordinates: "", excerpt: "", description: "",
  duration_days: 7, group_max: 8, difficulty: "moderata", price: 2500, currency: "EUR",
  dates_label: "", status: "draft", badge: "available", sort_order: 0,
  leader_slugs: [], images: [], videos: [], included: [], excluded: [],
  itinerary: [{ day: 1, title: "", description: "" }],
  translations: {},
};

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => (typeof s === "string" ? s.split("\n").map((x) => x.trim()).filter(Boolean) : s || []);
const toArr = (v) => (Array.isArray(v) ? v : fromLines(v));

export default function AdminTours() {
  const isAdmin = useAuth().user?.role === "admin";

  const [tours, setTours] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [langTab, setLangTab] = useState("it");
  const [draftInfo, setDraftInfo] = useState(null);
  const dirtyRef = useRef(false);

  const load = () => api.get("/admin/tours").then((r) => setTours(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/leaders").then((r) => setLeaders(r.data)).catch(() => {});
  }, []);

  const draftKey = (t) => `delos-draft-tour-${t && t.id ? t.id : "new"}`;
  const readDraft = (t) => {
    try {
      const raw = localStorage.getItem(draftKey(t));
      const d = raw ? JSON.parse(raw) : null;
      return d && d.form ? d : null;
    } catch (e) {
      return null;
    }
  };

  // Bozza automatica: salvataggio locale debounced mentre si modifica + flush se la pagina si chiude
  useEffect(() => {
    if (!editing) return undefined;
    const write = () => {
      if (draftInfo || !dirtyRef.current) return;
      try {
        localStorage.setItem(draftKey(editing), JSON.stringify({ form: editing, saved_at: new Date().toISOString() }));
      } catch (e) { /* archiviazione non disponibile */ }
    };
    const t = setTimeout(write, 800);
    const onUnload = (e) => {
      write();
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [editing, draftInfo]);

  const prepareTranslations = (trs) => {
    const out = {};
    for (const lg of ["en", "es"]) {
      const c = { ...(trs && trs[lg] ? trs[lg] : {}) };
      c.included = toLines(c.included);
      c.excluded = toLines(c.excluded);
      c.itinerary = (c.itinerary || []).map((d) => ({ ...d }));
      out[lg] = c;
    }
    return out;
  };

  const openNew = () => {
    setLangTab("it");
    dirtyRef.current = false;
    const base = { ...EMPTY, translations: prepareTranslations({}) };
    setEditing(base);
    const d = readDraft(base);
    setDraftInfo(d ? { key: draftKey(base), saved_at: d.saved_at, form: d.form } : null);
  };
  const openEdit = (t) => {
    setLangTab("it");
    dirtyRef.current = false;
    setEditing({
      ...t,
      translations: prepareTranslations(t.translations),
      leader_slugs: t.leader_slugs || [],
      images: toLines(t.images),
      videos: toLines(t.videos),
      included: toLines(t.included),
      excluded: toLines(t.excluded),
    });
    const d = readDraft(t);
    setDraftInfo(d ? { key: draftKey(t), saved_at: d.saved_at, form: d.form } : null);
  };

  const restoreDraft = () => {
    if (!draftInfo) return;
    setEditing({ ...draftInfo.form });
    dirtyRef.current = true;
    setDraftInfo(null);
    toast.success("Bozza ripristinata");
  };
  const discardDraft = () => {
    if (draftInfo) {
      try { localStorage.removeItem(draftInfo.key); } catch (e) { /* già assente */ }
    }
    setDraftInfo(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      const translations = {};
      for (const lg of ["en", "es"]) {
        const c = { ...(editing.translations && editing.translations[lg] ? editing.translations[lg] : {}) };
        c.included = fromLines(c.included);
        c.excluded = fromLines(c.excluded);
        c.itinerary = (c.itinerary || []).map((d, i) => ({ ...d, day: parseInt(d.day) || i + 1 })).filter((d) => d.title);
        const has = Object.entries(c).some(([k, v]) => (Array.isArray(v) ? v.length : v && typeof v === "string"));
        if (has) translations[lg] = c;
      }
      const payload = {
        ...editing,
        translations,
        duration_days: parseInt(editing.duration_days) || 1,
        group_max: parseInt(editing.group_max) || 1,
        price: parseFloat(editing.price) || 0,
        sort_order: parseInt(editing.sort_order) || 0,
        leader_slugs: toArr(editing.leader_slugs),
        images: fromLines(editing.images),
        videos: fromLines(editing.videos),
        included: fromLines(editing.included),
        excluded: fromLines(editing.excluded),
        itinerary: (editing.itinerary || []).map((d, i) => ({ ...d, day: parseInt(d.day) || i + 1 })).filter((d) => d.title),
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.computed;
      if (editing.id) {
        await api.put(`/admin/tours/${editing.id}`, payload);
        toast.success("Spedizione aggiornata");
      } else {
        await api.post("/admin/tours", payload);
        toast.success("Spedizione creata");
      }
      try { localStorage.removeItem(draftKey(editing)); } catch (e) { /* già assente */ }
      setDraftInfo(null);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare "${t.title}"? Operazione definitiva.`)) return;
    try {
      await api.delete(`/admin/tours/${t.id}`);
      toast.success("Spedizione eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publish = async (t) => {
    if (!window.confirm(`Pubblicare "${t.title}" sul sito pubblico? I visitatori la vedranno subito.`)) return;
    try {
      const payload = { ...t, status: "published" };
      delete payload.id;
      delete payload.created_at;
      delete payload.computed;
      await api.put(`/admin/tours/${t.id}`, payload);
      toast.success(`"${t.title}" è ora pubblica sul sito`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setF = (k, v) => { dirtyRef.current = true; setEditing((e) => ({ ...e, [k]: v })); };
  const setItin = (i, k, v) => { dirtyRef.current = true; setEditing((e) => ({ ...e, itinerary: e.itinerary.map((d, j) => (j === i ? { ...d, [k]: v } : d)) })); };
  const setFF = (i, k, v) => { dirtyRef.current = true; setEditing((e) => ({ ...e, form_fields: (e.form_fields || []).map((f, j) => (j === i ? { ...f, [k]: v } : f)) })); };
  const setFFTr = (i, k, v) => { dirtyRef.current = true; setEditing((e) => ({
    ...e,
    form_fields: (e.form_fields || []).map((f, j) => (j === i ? { ...f, translations: { ...(f.translations || {}), [langTab]: { ...((f.translations || {})[langTab] || {}), [k]: v } } } : f)),
  })); };
  const addFF = () => { dirtyRef.current = true; setEditing((e) => ({ ...e, form_fields: [...(e.form_fields || []), { id: Math.random().toString(36).slice(2, 10), label: "", type: "text", options: [], required: false }] })); };
  const removeFF = (i) => { dirtyRef.current = true; setEditing((e) => ({ ...e, form_fields: (e.form_fields || []).filter((_, j) => j !== i) })); };
  const curT = (editing && editing.translations ? editing.translations[langTab] : null) || {};
  const setTL = (k, v) => { dirtyRef.current = true; setEditing((e) => ({ ...e, translations: { ...e.translations, [langTab]: { ...(e.translations?.[langTab] || {}), [k]: v } } })); };
  const setTLItin = (i, k, v) => {
    dirtyRef.current = true;
    setEditing((e) => {
      const trs = { ...(e.translations || {}) };
      const c = { ...(trs[langTab] || {}) };
      c.itinerary = (c.itinerary || []).map((d, j) => (j === i ? { ...d, [k]: v } : d));
      trs[langTab] = c;
      return { ...e, translations: trs };
    });
  };

  return (
    <div data-testid="admin-tours">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl">Spedizioni</h1>
          <p className="text-cream2 text-sm mt-1">Crea e aggiorna i viaggi del catalogo, in italiano, inglese e spagnolo.</p>
        </div>
        <button onClick={openNew} className="btn-copper !px-5 !py-2.5" data-testid="tours-add-btn"><Plus className="h-4 w-4" /> Nuova spedizione</button>
      </div>

      <div className="card-dark mt-8 overflow-x-auto" data-testid="tours-table">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-white/5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-sand">
              <th className="px-5 py-4">Titolo</th>
              <th className="px-5 py-4">Luogo</th>
              <th className="px-5 py-4">Date</th>
              <th className="px-5 py-4">Prezzo</th>
              <th className="px-5 py-4">Stato</th>
              <th className="px-5 py-4" />
            </tr>
          </thead>
          <tbody>
            {tours.map((t) => (
              <tr key={t.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`tours-row-${t.slug}`}>
                <td className="px-5 py-4 text-cream font-serif text-lg">
                  {t.title}
                  <span className="ml-2 font-mono text-[9px] text-sand uppercase">
                    {["en", "es"].filter((lg) => t.translations && t.translations[lg] && Object.keys(t.translations[lg]).length).map((lg) => lg.toUpperCase()).join(" ") || "—"}
                  </span>
                </td>
                <td className="px-5 py-4 text-cream2">{t.location}, {t.country}</td>
                <td className="px-5 py-4 text-cream2">{t.dates_label}</td>
                <td className="px-5 py-4 text-copper">€{Number(t.price).toLocaleString("it-IT")}</td>
                <td className="px-5 py-4">
                  <span className={"font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border " + (t.status === "published" ? "border-green-500/40 text-green-400" : "border-white/10 text-sand")}>
                    {t.status === "published" ? "Pubblicato" : "Bozza"}
                  </span>
                  {t.status !== "published" && (
                    <button onClick={() => publish(t)} className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-copperlight border border-copper/30 px-3 py-1.5 hover:bg-copper/10 transition-colors" data-testid={`tours-publish-${t.slug}`}>
                      <Globe className="h-3 w-3" /> Pubblica
                    </button>
                  )}
                </td>
                <td className="px-5 py-4">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(t)} className="p-2 text-cream2 hover:text-copper" data-testid={`tours-edit-${t.slug}`}><Pencil className="h-4 w-4" /></button>
                    {isAdmin && (
                      <button onClick={() => remove(t)} className="p-2 text-cream2 hover:text-red-400" data-testid={`tours-delete-${t.slug}`}><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[90] bg-obsidian/90 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="tours-editor-modal">
          <div className="max-w-3xl mx-auto card-dark p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-serif text-2xl">{editing.id ? "Modifica spedizione" : "Nuova spedizione"}</h2>
              <div className="flex items-center gap-2">
                {["it", "en", "es"].map((lg) => (
                  <button
                    key={lg}
                    onClick={() => setLangTab(lg)}
                    data-testid={`tours-lang-tab-${lg}`}
                    className={"font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " + (langTab === lg ? "border-copper text-copperlight" : "border-white/10 text-sand hover:text-cream")}
                  >
                    {lg}
                  </button>
                ))}
                <button onClick={() => setEditing(null)} className="text-sand hover:text-cream ml-2" data-testid="tours-editor-close"><X className="h-5 w-5" /></button>
              </div>
            </div>

            {draftInfo && (
              <div className="border border-copper/30 bg-copper/10 p-4 flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="tours-draft-banner">
                <p className="text-sm text-cream">Bozza ritrovata del {new Date(draftInfo.saved_at).toLocaleString("it-IT")} — vuoi ripristinarla?</p>
                <div className="flex gap-2">
                  <button onClick={restoreDraft} className="btn-copper !px-4 !py-2 !text-xs" data-testid="tours-draft-restore">Ripristina</button>
                  <button onClick={discardDraft} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="tours-draft-discard">Scarta</button>
                </div>
              </div>
            )}

            {langTab === "it" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className="label">Titolo *</label><input className="field" value={editing.title} onChange={(e) => setF("title", e.target.value)} data-testid="tours-field-title" /></div>
                <div><label className="label">Slug (URL) *</label><input className="field" placeholder="amazzonia" value={editing.slug} onChange={(e) => setF("slug", e.target.value)} data-testid="tours-field-slug" /></div>
                <div><label className="label">Etichetta date</label><input className="field" placeholder="Marzo 2026 · Edizione 03" value={editing.dates_label} onChange={(e) => setF("dates_label", e.target.value)} data-testid="tours-field-dates" /></div>
                <div><label className="label">Località</label><input className="field" value={editing.location} onChange={(e) => setF("location", e.target.value)} data-testid="tours-field-location" /></div>
                <div><label className="label">Paese</label><input className="field" value={editing.country} onChange={(e) => setF("country", e.target.value)} data-testid="tours-field-country" /></div>
                <div><label className="label">Destinazione in Home (testo a scorrimento)</label><input className="field" placeholder="Es. Amazzonia — se vuoto usa la località" value={editing.destination_name || ""} onChange={(e) => setF("destination_name", e.target.value)} data-testid="tours-field-destination" /></div>
                <div><label className="label">Coordinate (Home)</label><input className="field" placeholder="Es. 3°S 73°W" value={editing.coordinates || ""} onChange={(e) => setF("coordinates", e.target.value)} data-testid="tours-field-coordinates" /></div>
                <div className="sm:col-span-2"><label className="label">Estratto (card)</label><textarea className="field resize-none" rows="2" value={editing.excerpt} onChange={(e) => setF("excerpt", e.target.value)} data-testid="tours-field-excerpt" /></div>
                <div className="sm:col-span-2"><label className="label">Descrizione completa</label><textarea className="field resize-none" rows="5" value={editing.description} onChange={(e) => setF("description", e.target.value)} data-testid="tours-field-description" /></div>
                <div><label className="label">Durata (giorni)</label><input type="number" className="field" value={editing.duration_days} onChange={(e) => setF("duration_days", e.target.value)} data-testid="tours-field-duration" /></div>
                <div><label className="label">Gruppo max</label><input type="number" className="field" value={editing.group_max} onChange={(e) => setF("group_max", e.target.value)} data-testid="tours-field-group" /></div>
                <div><label className="label">Difficoltà</label>
                  <select className="field" value={editing.difficulty} onChange={(e) => setF("difficulty", e.target.value)} data-testid="tours-field-difficulty">
                    <option value="accessibile">Accessibile</option><option value="moderata">Moderata</option><option value="impegnativa">Impegnativa</option>
                  </select>
                </div>
                <div><label className="label">Prezzo (EUR)</label><input type="number" className="field" value={editing.price} onChange={(e) => setF("price", e.target.value)} data-testid="tours-field-price" /></div>
                <div><label className="label">Stato</label>
                  <select className="field" value={editing.status} onChange={(e) => setF("status", e.target.value)} data-testid="tours-field-status">
                    <option value="draft">Bozza</option><option value="published">Pubblicato</option>
                  </select>
                </div>
                <div><label className="label">Badge disponibilità</label>
                  <select className="field" value={editing.badge} onChange={(e) => setF("badge", e.target.value)} data-testid="tours-field-badge">
                    <option value="available">Posti disponibili</option><option value="last_spots">Ultimi posti</option><option value="sold_out">Sold out</option>
                  </select>
                </div>
                <div className="sm:col-span-2" data-testid="tours-field-leaders">
                  <label className="label">Tour leader della spedizione (uno o più, in ordine di visualizzazione)</label>
                  <LeaderPicker value={toArr(editing.leader_slugs)} leaders={leaders} onChange={(v) => setF("leader_slugs", v)} />
                </div>
                <div className="sm:col-span-2"><label className="label">Immagini (URL, uno per riga)</label><textarea className="field resize-none" rows="4" value={editing.images} onChange={(e) => setF("images", e.target.value)} data-testid="tours-field-images" /></div>
                <div className="sm:col-span-2"><label className="label">Video (URL embed, uno per riga)</label><textarea className="field resize-none" rows="3" value={editing.videos} onChange={(e) => setF("videos", e.target.value)} data-testid="tours-field-videos" /></div>
                <div><label className="label">Nel prezzo (uno per riga)</label><textarea className="field resize-none" rows="4" value={editing.included} onChange={(e) => setF("included", e.target.value)} data-testid="tours-field-included" /></div>
                <div><label className="label">Non incluso (uno per riga)</label><textarea className="field resize-none" rows="4" value={editing.excluded} onChange={(e) => setF("excluded", e.target.value)} data-testid="tours-field-excluded" /></div>
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-3">
                    <label className="label !mb-0">Programma giorno per giorno (IT)</label>
                    <button
                      className="font-mono text-[10px] uppercase tracking-widest text-copperlight border border-copper/30 px-3 py-1.5"
                      onClick={() => setF("itinerary", [...(editing.itinerary || []), { day: (editing.itinerary || []).length + 1, title: "", description: "" }])}
                      data-testid="tours-add-day"
                    >
                      + Giorno
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(editing.itinerary || []).map((d, i) => (
                      <div key={i} className="border border-white/5 bg-obsidian2 p-4" data-testid={`tours-day-row-${i + 1}`}>
                        <div className="flex gap-3 items-center mb-2">
                          <span className="font-mono text-copper text-xs">G{d.day}</span>
                          <input className="field !py-1.5" placeholder="Titolo giornata" value={d.title} onChange={(e) => setItin(i, "title", e.target.value)} data-testid={`tours-day-title-${i + 1}`} />
                          <button onClick={() => setF("itinerary", editing.itinerary.filter((_, j) => j !== i))} className="text-sand hover:text-red-400 shrink-0" data-testid={`tours-day-remove-${i + 1}`}><X className="h-4 w-4" /></button>
                        </div>
                        <textarea className="field resize-none !py-1.5" rows="2" placeholder="Descrizione giornata" value={d.description} onChange={(e) => setItin(i, "description", e.target.value)} data-testid={`tours-day-desc-${i + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {langTab !== "it" && (
              <div className="space-y-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-sand">Traduzione {langTab.toUpperCase()} — lascia vuoto per usare il testo italiano.</p>
                <div><label className="label">Titolo</label><input className="field" value={curT.title || ""} onChange={(e) => setTL("title", e.target.value)} data-testid={`tours-tr-title-${langTab}`} /></div>
                <div><label className="label">Estratto (card)</label><textarea className="field resize-none" rows="2" value={curT.excerpt || ""} onChange={(e) => setTL("excerpt", e.target.value)} data-testid={`tours-tr-excerpt-${langTab}`} /></div>
                <div><label className="label">Descrizione completa</label><textarea className="field resize-none" rows="5" value={curT.description || ""} onChange={(e) => setTL("description", e.target.value)} data-testid={`tours-tr-description-${langTab}`} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div><label className="label">Località</label><input className="field" value={curT.location || ""} onChange={(e) => setTL("location", e.target.value)} data-testid={`tours-tr-location-${langTab}`} /></div>
                  <div><label className="label">Paese</label><input className="field" value={curT.country || ""} onChange={(e) => setTL("country", e.target.value)} data-testid={`tours-tr-country-${langTab}`} /></div>
                  <div><label className="label">Etichetta date</label><input className="field" value={curT.dates_label || ""} onChange={(e) => setTL("dates_label", e.target.value)} data-testid={`tours-tr-dates-${langTab}`} /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="label !mb-0">Programma giorno per giorno ({langTab.toUpperCase()})</label>
                    <button
                      className="font-mono text-[10px] uppercase tracking-widest text-copperlight border border-copper/30 px-3 py-1.5"
                      onClick={() => setTL("itinerary", [...(curT.itinerary || []), { day: (curT.itinerary || []).length + 1, title: "", description: "" }])}
                      data-testid={`tours-tr-add-day-${langTab}`}
                    >
                      + Giorno
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(curT.itinerary || []).map((d, i) => (
                      <div key={i} className="border border-white/5 bg-obsidian2 p-4">
                        <div className="flex gap-3 items-center mb-2">
                          <span className="font-mono text-copper text-xs">G{d.day}</span>
                          <input className="field !py-1.5" placeholder="Titolo giornata" value={d.title || ""} onChange={(e) => setTLItin(i, "title", e.target.value)} data-testid={`tours-tr-day-title-${langTab}-${i + 1}`} />
                          <button onClick={() => setTL("itinerary", (curT.itinerary || []).filter((_, j) => j !== i))} className="text-sand hover:text-red-400 shrink-0"><X className="h-4 w-4" /></button>
                        </div>
                        <textarea className="field resize-none !py-1.5" rows="2" placeholder="Descrizione giornata" value={d.description || ""} onChange={(e) => setTLItin(i, "description", e.target.value)} data-testid={`tours-tr-day-desc-${langTab}-${i + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
                <div><label className="label">Nel prezzo (uno per riga)</label><textarea className="field resize-none" rows="4" value={curT.included || ""} onChange={(e) => setTL("included", e.target.value)} data-testid={`tours-tr-included-${langTab}`} /></div>
                <div><label className="label">Non incluso (uno per riga)</label><textarea className="field resize-none" rows="4" value={curT.excluded || ""} onChange={(e) => setTL("excluded", e.target.value)} data-testid={`tours-tr-excluded-${langTab}`} /></div>
                <div className="sm:col-span-2 pt-2 border-t border-white/5">
                  <p className="eyebrow mb-4">Modulo di partecipazione — traduzione {langTab.toUpperCase()}</p>
                  <div className="space-y-3">
                    {(editing.form_fields || []).map((f, i) => {
                      const tr = (f.translations || {})[langTab] || {};
                      return (
                        <div key={f.id || i} className="border border-white/5 bg-obsidian2 p-4 space-y-2" data-testid={`tours-tr-formfield-${langTab}-${i + 1}`}>
                          <p className="font-mono text-[10px] text-sand">IT: {f.label || "—"}</p>
                          <input className="field !py-1.5" placeholder={`Domanda in ${langTab.toUpperCase()} (vuoto = testo italiano)`} value={tr.label || ""} onChange={(e) => setFFTr(i, "label", e.target.value)} data-testid={`tours-tr-formfield-label-${langTab}-${i + 1}`} />
                          {f.type === "select" && (
                            <input className="field !py-1.5" placeholder={`Opzioni in ${langTab.toUpperCase()} (separato da virgole)`} value={(tr.options || []).join(", ")} onChange={(e) => setFFTr(i, "options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`tours-tr-formfield-options-${langTab}-${i + 1}`} />
                          )}
                        </div>
                      );
                    })}
                    {(editing.form_fields || []).length === 0 && <p className="text-sand text-xs">Nessun campo personalizzato da tradurre.</p>}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <label className="label !mb-0">Modulo di partecipazione (personalizzabile)</label>
                <button
                  className="font-mono text-[10px] uppercase tracking-widest text-copperlight border border-copper/30 px-3 py-1.5"
                  onClick={addFF}
                  data-testid="tours-add-form-field"
                >
                  + Campo
                </button>
              </div>
              <p className="font-mono text-[10px] text-sand -mt-1 mb-3">Il modulo è mostrato nella pagina Candidatura di questa spedizione.</p>
              <div className="space-y-3">
                {(editing.form_fields || []).map((f, i) => (
                  <div key={f.id || i} className="border border-white/5 bg-obsidian2 p-4 grid grid-cols-12 gap-2 items-center" data-testid={`tours-formfield-row-${i + 1}`}>
                    <input className="field !py-1.5 col-span-12 sm:col-span-5" placeholder="Domanda (es. Perché vuoi partecipare?)" value={f.label || ""} onChange={(e) => setFF(i, "label", e.target.value)} data-testid={`tours-formfield-label-${i + 1}`} />
                    <select className="field !py-1.5 col-span-4 sm:col-span-2" value={f.type || "text"} onChange={(e) => setFF(i, "type", e.target.value)} data-testid={`tours-formfield-type-${i + 1}`}>
                      <option value="text">Testo</option>
                      <option value="textarea">Testo lungo</option>
                      <option value="select">Scelta multipla</option>
                      <option value="checkbox">Sì / No</option>
                      <option value="number">Numero</option>
                    </select>
                    <input className="field !py-1.5 col-span-8 sm:col-span-3" placeholder="Opzioni (separato da virgole)" value={(f.options || []).join(", ")} onChange={(e) => setFF(i, "options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`tours-formfield-options-${i + 1}`} />
                    <label className="col-span-3 sm:col-span-1 flex items-center justify-center gap-1 text-[10px] font-mono text-sand">
                      <input type="checkbox" className="accent-[#C86D3B] w-3.5 h-3.5" checked={!!f.required} onChange={(e) => setFF(i, "required", e.target.checked)} data-testid={`tours-formfield-required-${i + 1}`} />
                      Obblig.
                    </label>
                    <button onClick={() => removeFF(i)} className="col-span-1 text-sand hover:text-red-400 justify-self-center" data-testid={`tours-formfield-remove-${i + 1}`}><X className="h-4 w-4" /></button>
                  </div>
                ))}
                {(editing.form_fields || []).length === 0 && <p className="text-sand text-xs">Nessun campo personalizzato: il modulo chiederà solo nome, email e telefono.</p>}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setEditing(null)} className="btn-ghost !px-5 !py-2.5" data-testid="tours-cancel">Annulla</button>
              <button onClick={save} disabled={busy} className="btn-copper !px-6 !py-2.5 disabled:opacity-50" data-testid="tours-save">{busy ? "Salvo…" : "Salva"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LeaderPicker({ value, leaders, onChange }) {
  const [pick, setPick] = useState("");
  const selected = value.map((slug) => leaders.find((l) => l.slug === slug) || { slug, name: slug, missing: true });
  const available = leaders.filter((l) => !value.includes(l.slug));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => {
    if (pick && !value.includes(pick)) onChange([...value, pick]);
    setPick("");
  };
  return (
    <div className="bg-obsidian2 border border-white/10 p-3 space-y-2">
      {selected.length === 0 && <p className="text-xs text-sand" data-testid="tours-leaders-empty">Nessun tour leader associato.</p>}
      {selected.map((l, i) => (
        <div key={l.slug} className="flex items-center gap-3 border border-white/10 px-3 py-2" data-testid={`tours-leader-row-${l.slug}`}>
          <span className="font-mono text-[10px] text-copper w-5">{i + 1}</span>
          <span className={"text-sm flex-1 truncate " + (l.missing ? "text-red-300" : "text-cream")}>{l.name}{l.missing ? " (slug non trovato)" : ""}{l.role ? <span className="text-sand text-xs"> · {l.role}</span> : null}</span>
          <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-cream2 hover:text-copper disabled:opacity-30" data-testid={`tours-leader-up-${l.slug}`}><ChevronUp className="h-4 w-4" /></button>
          <button type="button" onClick={() => move(i, 1)} disabled={i === selected.length - 1} className="p-1 text-cream2 hover:text-copper disabled:opacity-30" data-testid={`tours-leader-down-${l.slug}`}><ChevronDown className="h-4 w-4" /></button>
          <button type="button" onClick={() => onChange(value.filter((s) => s !== l.slug))} className="p-1 text-cream2 hover:text-red-400" data-testid={`tours-leader-remove-${l.slug}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <select className="field" value={pick} onChange={(e) => setPick(e.target.value)} data-testid="tours-leader-select">
          <option value="">Aggiungi un tour leader…</option>
          {available.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
        </select>
        <button type="button" onClick={add} disabled={!pick} className="btn-ghost !px-4 !py-2 !text-xs shrink-0 disabled:opacity-40" data-testid="tours-leader-add"><UserPlus className="h-4 w-4" /> Aggiungi</button>
      </div>
    </div>
  );
}
